import { Sheet, SheetContent, SheetTitle, SheetDescription } from './ui/sheet';
import { ChevronLeft, PlugZap } from 'lucide-react';
import { Button } from './ui/button';
import { useCallback, useEffect, useState } from 'react';
import type { ChargingLocation, DetailedChargePoint } from '../../types';
import { ChargePointConnectors, type ChargePointStatusInfo } from './ChargePointConnectors';

/**
 * ChargePointDetailSheet
 *
 * A single charge point's guns, reached by QR deep link
 * (/app/location/:locationId/chargepoint/:chargepointId).
 *
 * Browsing no longer comes through here — LocationDetailSheet renders the guns
 * inline, since 87% of locations have exactly one charge point and this sheet was
 * a wasted tap on the way to them. The gun cards and their fetching are shared
 * with that path via ChargePointConnectors, so the two cannot drift.
 */

interface ChargePointDetailSheetProps {
  chargepoint: DetailedChargePoint | null;
  location?: ChargingLocation | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBack?: () => void;
  onStartCharging?: (chargePointId: string, connectorId: string) => void;
  isStartingCharge?: boolean;
  /**
   * Gun number from the scanned sticker's ?gun= param — the connector the user is
   * physically standing at.
   */
  scannedGun?: string;
  isLoading?: boolean;
}

export function ChargePointDetailSheet({
  chargepoint,
  open,
  onOpenChange,
  onBack,
  onStartCharging,
  isStartingCharge = false,
  scannedGun,
  isLoading = false,
}: ChargePointDetailSheetProps) {
  // The charger name, specs, price and online pill are all on the child's header
  // now, so this sheet keeps only what the child cannot know: the loading and
  // empty states, and the way back.
  const [connectorCount, setConnectorCount] = useState(0);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [statusFailed, setStatusFailed] = useState(false);
  const [expanded, setExpanded] = useState<{ id: string | null; byUser: boolean }>({
    id: null,
    byUser: false,
  });

  const handleStatusResolved = useCallback(
    (info: ChargePointStatusInfo) => {
      setConnectorCount(info.connectorIds.length);
      setStatusLoaded(info.loaded);
      setStatusFailed(!!info.snapshotFailed);
      setExpanded((prev) => {
        if (prev.id !== null) return prev;
        // A sticker that names its gun beats any guess: the user is standing at
        // that connector. Open it even when it is busy or offline — saying so is
        // the useful answer, and silently opening a sibling is not. Only when the
        // number matches nothing do we fall back to guessing.
        const scanned = scannedGun ? info.connectorIdsByGun[scannedGun] : undefined;
        const pick =
          scanned ??
          info.firstActionableId ??
          (info.connectorIds.length === 1 ? info.connectorIds[0] : null);
        return pick === null ? prev : { id: pick, byUser: false };
      });
    },
    [scannedGun]
  );

  useEffect(() => {
    if (!open) {
      setExpanded({ id: null, byUser: false });
      setStatusLoaded(false);
      setStatusFailed(false);
    }
  }, [open]);

  const handleToggleConnector = useCallback((connectorId: string) => {
    setExpanded((prev) => ({ id: prev.id === connectorId ? null : connectorId, byUser: true }));
  }, []);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      onOpenChange(false);
    }
  };

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="px-5 py-6 animate-pulse">
      <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-3/4 mb-4" />
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-6" />
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div key={i} className="bg-gray-100 dark:bg-gray-800 rounded-2xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-xl" />
              <div>
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-20 mb-2" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Not found UI component
  const NotFoundUI = () => (
    <div className="flex flex-col items-center justify-center h-full px-5 py-12">
      <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
        <PlugZap className="w-10 h-10 text-gray-400 dark:text-gray-500" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Charge Point Not Found</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
        The charge point you're looking for doesn't exist or may have been removed.
      </p>
      <Button onClick={handleBack} variant="outline" className="px-6">
        <ChevronLeft className="w-4 h-4 mr-2" />
        Go Back
      </Button>
    </div>
  );

  // No EVSE Available UI component
  const NoEVSEUI = () => (
    <div className="flex flex-col items-center justify-center px-5 py-12">
      <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
        <PlugZap className="w-10 h-10 text-gray-400 dark:text-gray-500" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No EVSE Available</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
        This charge point currently has no active connectors. Please check back later or contact support.
      </p>
      <Button onClick={handleBack} variant="outline" className="px-6">
        <ChevronLeft className="w-4 h-4 mr-2" />
        Go Back
      </Button>
    </div>
  );

  const showSkeleton = isLoading || (open && !!chargepoint && !statusLoaded);
  const showNoEvse = !isLoading && !!chargepoint && statusLoaded && !statusFailed && connectorCount === 0;
  const showBody = !isLoading && !!chargepoint;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh] rounded-t-[32px] border-0 p-0 bg-white dark:bg-gray-900" showCloseButton={false}>
        <SheetTitle className="sr-only">Charge Point Details</SheetTitle>
        <SheetDescription className="sr-only">
          Select a connector to start charging
        </SheetDescription>

        {/* Handle Bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
        </div>

        {showSkeleton && <LoadingSkeleton />}

        {!isLoading && !chargepoint && <NotFoundUI />}

        {showNoEvse && <NoEVSEUI />}

        {/*
          The child stays mounted underneath the skeleton — it is what fetches the
          status the skeleton is waiting on. `hidden` keeps it out of view without
          unmounting and restarting the fetch.
        */}
        {showBody && (
          <div className={showSkeleton || showNoEvse ? 'hidden' : undefined}>
            {/* Header with Back Button */}
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span className="font-medium">Back</span>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto px-5 pt-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]" style={{ maxHeight: 'calc(85vh - 160px)' }}>
              <ChargePointConnectors
                chargePoint={chargepoint!}
                enabled={open}
                showHeader
                expandedConnectorId={expanded.id}
                expandedByUser={expanded.byUser}
                onToggleConnector={handleToggleConnector}
                onStartCharging={onStartCharging}
                isStartingCharge={isStartingCharge}
                onStatusResolved={handleStatusResolved}
              />
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
